<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../lib/api';
  import { session } from '../lib/session.svelte';
  import { decryptPrivateCleartext } from '../lib/privateCleartext';
  import ActivityRow from './ActivityRow.svelte';
  import type { Activity } from '../../../shared/types';

  interface Props {
    tag: string;
    onBack?: () => void;
  }
  let { tag, onBack }: Props = $props();

  // Normalise the URL-supplied tag the same way the server does so the
  // visible heading matches what was actually filtered. The server lowercases
  // and trims; tags in payloads are stored lowercase via TagInput too.
  const needle = $derived(tag.trim().toLowerCase());

  let activities: Activity[] = $state([]);
  let loading = $state(true);
  let error: string | null = $state(null);

  onMount(load);

  async function load() {
    loading = true;
    error = null;
    try {
      activities = await api.listActivities();
    } catch {
      error = 'Kunne ikke laste aktiviteter.';
    } finally {
      loading = false;
    }
  }

  /** Pre-decrypt private rows once so we can filter by tag and let
   *  ActivityRow render without redoing the work. */
  const privateCleartext = $derived(decryptPrivateCleartext(activities, session.dek));

  function hasTag(a: Activity): boolean {
    if (a.visibility === 'private') {
      const p = privateCleartext.get(a.id);
      return !!p && p.tags.includes(needle);
    }
    return a.tags.includes(needle);
  }

  const matched = $derived(activities.filter(hasTag));

  // For removed/changed rows from ActivityRow, mutate the local list. Same
  // contracts as Home.svelte.
  function onDeleted(id: string) {
    activities = activities.filter((a) => a.id !== id);
  }
  function onChanged(a: Activity) {
    activities = activities.map((x) => (x.id === a.id ? a : x));
  }
</script>

<section aria-labelledby="tag-h">
  {#if onBack}
    <div class="row" style="margin-bottom: 1rem;">
      <button type="button" onclick={onBack}>← Tilbake</button>
    </div>
  {/if}

  <h2 id="tag-h" style="font-size: 1.5rem;">
    Etikett: <span class="tag" style="font-size: inherit; padding: 0.2rem 0.6rem;">{needle}</span>
  </h2>
  <p class="muted">
    {#if session.user}
      Aktiviteter med denne etiketten du har tilgang til — egne private,
      offentlige, anonyme, og fra venner.
    {:else}
      Offentlige og anonyme aktiviteter med denne etiketten.
    {/if}
  </p>

  {#if loading}
    <p class="muted">Laster …</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if matched.length === 0}
    <p class="muted">Ingen aktiviteter med denne etiketten ennå.</p>
  {:else}
    {#each matched as a (a.id)}
      <ActivityRow
        activity={a}
        privateCleartext={privateCleartext.get(a.id) ?? null}
        onDeleted={onDeleted}
        onChanged={onChanged}
      />
    {/each}
  {/if}
</section>
